import React, { Component } from 'react';
import { Row, Col, Container } from 'react-bootstrap';
import '../css/style.css';
import Preloader from '../appshell/Preloader/Preloader';
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css';

class Sightline extends Component {
    constructor(props) {
        super(props);
        this.state = { spinner: true }
    }

    componentDidMount() {
        setTimeout(() => {
            this.setState({ spinner: false });
        }, 1000);
    }

    render() {
        return (
            <section>
                {this.state.spinner ? <Preloader /> : true}

                <Row className="hero-section d-flex position-relative align-items-center">
                    <Container>
                        <Row>
                            <div className="col-12 col-md-6 hero-content">
                                <h1>ICF Sightline Homes</h1>
                                <p className="mb-0 mt-4">
                                    <strong>My Role</strong>: Product Designer & Design System Lead
                                </p>
                                <p className="mb-0">
                                    <strong>Responsibilities</strong>: White-label architecture, UI/UX strategy, variable-based system design, stakeholder management across multiple utility clients.
                                </p>
                                <p className="mb-0"><strong>Platform</strong>: Responsive Web (B2B2C)</p>
                                <p>
                                    <strong>Deliverables</strong>: 
                                    <span className="d-block mt-1">
                                        <a href="https://pepco-homes.icfpowerguide.com/" target="_blank" rel="noopener noreferrer" className="mr-3 header-link">Pepco PowerGuide</a>
                                        <a href="https://delmarva-homes.icfpowerguide.com/" target="_blank" rel="noopener noreferrer" className="mr-3 header-link">Delmarva PowerGuide</a>
                                        <a href="https://smeco-homes.icfpowerguide.com/" target="_blank" rel="noopener noreferrer" className="header-link">SMECO PowerGuide</a>
                                    </span>
                                </p>
                            </div>
                        </Row>
                    </Container>

                    <div className="col-12 hero-image">
                        <div className="hero-image-container">
                            <LazyLoadImage
                                effect="blur"
                                src="./assets/img/sightline/sightline-hero.webp"
                                className="hero-image-cover"
                                alt="ICF Sightline Homes Hero"
                            />
                        </div>
                    </div>
                </Row>

                <section className="section1 section">
                    <Container>
                        <Row className="align-items-start">
                            <Col xs={12}>
                                <h2>The Challenge: Overcoming Decision Paralysis</h2>
                                <p>
                                    Residential energy upgrades are often high-friction, high-cost decisions. Customers are presented with a dizzying array of equipment—heat pumps, water heaters, insulation—each with varying costs, rebates, and long-term savings.
                                </p>
                                <p>
                                    Our challenge was to build a <strong>white-label product</strong> that could be deployed rapidly for dozens of utility clients while solving a core user problem: <strong>cognitive overload</strong>. Early research showed that users struggled to track how different combinations of equipment affected their budget and environmental impact, often abandoning the process before completing an audit.
                                </p>
                            </Col>
                        </Row>
                        <Row className="mt-5">
                            <Col xs={12}>
                                <LazyLoadImage alt="Guided Data Collection" effect="blur" src="./assets/img/sightline/sightline-steps.webp" className="img-fluid rounded shadow-sm" />
                                <p className="label mt-3" style={{ textAlign: 'center' }}><strong>Guided Experience:</strong> Translating home technical data into clear consumer choices.</p>
                            </Col>
                        </Row>
                    </Container>
                </section>

                <section className="section2 section">
                    <Container>
                        <Row className="align-items-start">
                            <Col xs={12}>
                                <h2>The Comparison Engine: A New Mental Model</h2>
                                <p>
                                    To solve for cognitive overload, we moved away from static result pages. We designed the <strong>Comparison Engine</strong>—a session-based interaction model where every equipment configuration a user explores is captured as a "slide" in a persistent tray.
                                </p>
                                <p>
                                    By providing a "safe space" for experimentation, we enabled users to explore "what-if" scenarios: <em>"What if I do insulation now, but wait on the heat pump?"</em> vs <em>"What if I do both and use the maximum rebate?"</em> This transformed the experience from a one-way calculator into a <strong>dynamic roadmap builder</strong>, significantly increasing user confidence and audit completion rates.
                                </p>
                            </Col>
                        </Row>
                        <Row className="mt-5">
                            <Col xs={12}>
                                <LazyLoadImage alt="Results and Comparison" effect="blur" src="./assets/img/sightline/sightline-result.webp" className="img-fluid rounded shadow-sm" />
                                <p className="label mt-3" style={{ textAlign: 'center' }}><strong>Persistent Tray:</strong> Allowing side-by-side comparison of complex financial and environmental outcomes.</p>
                            </Col>
                        </Row>
                    </Container>
                </section>

                <section className="section1 section">
                    <Container>
                        <Row className="align-items-start">
                            <Col xs={12} lg={9}>
                                <h2>Scalable White-Label Architecture</h2>
                                <p>
                                    As a product sold to multiple utility providers, the UI needed to be highly elastic. We developed a robust <strong>variable-based architecture</strong> that goes beyond simple "skinning." 
                                </p>
                                <p>
                                    By managing branding, regional logic, and localized utility rates through a centralized configuration system, we can deploy a fully branded, compliance-ready instance for a new client in a fraction of the traditional development time. This architectural approach allows us to maintain a single core codebase while delivering highly customized experiences for clients like SMECO, Pepco, and Washington Gas.
                                </p>
                            </Col>
                            <Col xs={12} lg={3}>
                                <LazyLoadImage alt="Sightline Variable System" effect="blur" src="./assets/img/sightline/slightline-variables.webp" className="img-fluid rounded shadow-sm" />
                                <p className="label mt-3" style={{ textAlign: 'center' }}><strong>Variable Architecture:</strong> Centralized control over branding and regional logic.</p>
                            </Col>
                        </Row>

                        <Row className="mt-5 pt-4">
                            <Col xs={12}>
                                <h3 className="mb-4 text-left">Multi-Client Deployments</h3>
                                <p className="text-left mb-5">
                                    Our "One Core, Many Skins" approach has allowed for rapid expansion. Each deployment maintains the core Comparison Engine while adapting to the unique regulatory and visual requirements of the utility brand.
                                </p>
                            </Col>
                            <Col xs={12} md={4} className="mb-4">
                                <a href="https://pepco-homes.icfpowerguide.com/" target="_blank" rel="noopener noreferrer">
                                    <LazyLoadImage alt="Pepco Instance" effect="blur" src="./assets/img/sightline/sightline-pepco.webp" className="img-fluid rounded shadow-sm border" />
                                </a>
                                <p className="label mt-2" style={{ textAlign: 'center' }}><strong>Pepco PowerGuide</strong><br/>Optimized for regional urban energy profiles.</p>
                            </Col>
                            <Col xs={12} md={4} className="mb-4">
                                <a href="https://smeco-homes.icfpowerguide.com/" target="_blank" rel="noopener noreferrer">
                                    <LazyLoadImage alt="SMECO Instance" effect="blur" src="./assets/img/sightline/sightline-smeco.webp" className="img-fluid rounded shadow-sm border" />
                                </a>
                                <p className="label mt-2" style={{ textAlign: 'center' }}><strong>SMECO PowerGuide</strong><br/>Custom flows for energy cooperative members.</p>
                            </Col>
                            <Col xs={12} md={4} className="mb-4">
                                <LazyLoadImage alt="Washington Gas Instance" effect="blur" src="./assets/img/sightline/sightline-wg.webp" className="img-fluid rounded shadow-sm border" />
                                <p className="label mt-2" style={{ textAlign: 'center' }}><strong>Washington Gas</strong><br/>Tailored for specialized fuel-efficiency metrics.</p>
                            </Col>
                        </Row>
                    </Container>
                </section>

                <section className="section2 section">
                    <Container>
                        <Row className="justify-content-center">
                            <Col xs={12} md={8} className="text-center">
                                <h2>Impact & Future Expansion</h2>
                                <p>
                                    Since its launch, Sightline Homes has become the primary engagement tool for multiple utilities, seeing a marked increase in completed audits and higher user satisfaction compared to previous legacy tools. 
                                </p>
                                <p>
                                    Future roadmaps include automated rebate submissions directly through the platform and the integration of live billing data to provide even higher precision in savings projections.
                                </p>
                            </Col>
                        </Row>
                    </Container>
                </section>
            </section>
        );
    }
}

export default Sightline;
